import { client } from '@/shared/lib/client';

/**
 * 认证服务API接口
 */

// API响应类型定义
export interface ApiResponse<T = any> {
  status: number;
  message: string;
  data: T;
}

// 邮箱验证码请求参数
export interface SendCodeRequest {
  email: string;
}

// 邮箱注册请求参数
export interface EmailRegisterRequest {
  email: string;
  code: string;
  password: string;
  confirmPassword: string;
  username?: string;
  displayName?: string;
  auth_ticket_id?: string;
  product?: string;
}

// 邮箱登录请求参数
export interface EmailLoginRequest {
  email: string;
  password: string;
  auth_ticket_id?: string;
  product?: string;
}

// 忘记密码请求参数
export interface ForgotPasswordRequest {
  email: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
}

// 用户信息类型
export interface UserInfo {
  userId: string;
  username: string;
  displayName: string;
  email: string;
  avatar: string;
}

/**
 * 发送邮箱验证码
 * @param email 邮箱地址
 * @returns Promise<ApiResponse>
 */
export const sendEmailCode = async (email: string): Promise<ApiResponse> => {
  try {
    const response = (await client({
      url: '/api/auth/email/send-code',
      method: 'POST',
      data: { email },
    })) as ApiResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to send verification code');
  }
};

/**
 * 邮箱注册
 * @param data 注册数据
 * @returns Promise<ApiResponse>
 */
export const emailRegister = async (data: EmailRegisterRequest): Promise<ApiResponse> => {
  try {
    const response = (await client({
      url: '/api/auth/email/register',
      method: 'POST',
      data,
    })) as ApiResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Registration failed');
  }
};

/**
 * 邮箱密码登录
 * @param data 登录数据
 * @returns Promise<ApiResponse>
 */
export const emailLogin = async (data: EmailLoginRequest): Promise<ApiResponse> => {
  try {
    const response = (await client({
      url: '/api/auth/email/login',
      method: 'POST',
      data,
    })) as ApiResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Login failed');
  }
};

/**
 * 忘记密码
 * @param data 重置密码数据
 * @returns Promise<ApiResponse>
 */
export const forgotPassword = async (data: ForgotPasswordRequest): Promise<ApiResponse> => {
  try {
    const response = (await client({
      url: '/api/auth/email/forgot-password',
      method: 'POST',
      data,
    })) as ApiResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Password reset failed');
  }
};

/**
 * 获取当前用户信息
 * @returns Promise<ApiResponse<UserInfo>>
 */
export const getCurrentUser = async (): Promise<ApiResponse<UserInfo>> => {
  try {
    const response = (await client({
      url: '/api/user/me',
      method: 'GET',
    })) as ApiResponse<UserInfo>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get user information');
  }
};

/**
 * 用户登出
 * @returns Promise<ApiResponse>
 */
export const logout = async (): Promise<ApiResponse> => {
  try {
    const response = (await client({
      url: '/api/user/logout',
      method: 'GET',
    })) as ApiResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Logout failed');
  }
};

/**
 * 刷新Token
 * @returns Promise<ApiResponse>
 */
export const refreshToken = async (): Promise<ApiResponse> => {
  try {
    const response = (await client({
      url: '/api/user/refreshToken',
      method: 'GET',
    })) as ApiResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Token refresh failed');
  }
};

/**
 * GitHub登录跳转
 * 直接跳转到GitHub授权页面
 */
export const githubLogin = (ticket?: string, product?: string): void => {
  const query = [ticket && `auth_ticket_id=${ticket}`, product && `product=${product}`]
    .filter(Boolean)
    .join('&');
  window.location.href = `/api/user/login${query ? `?${query}` : ''}`;
};

/**
 * Google登录跳转
 * 直接跳转到Google授权页面
 */
export const googleLogin = (ticket?: string, product?: string): void => {
  const query = [ticket && `auth_ticket_id=${ticket}`, product && `product=${product}`]
    .filter(Boolean)
    .join('&');
  window.location.href = `/api/user/login/google${query ? `?${query}` : ''}`;
};

/**
 * 获取用户认证票据信息
 * @param authTicketId 票据ID
 * @returns Promise<ApiResponse>
 */
export const getUserAuthToken = async (authTicketId: string): Promise<ApiResponse> => {
  try {
    const response = (await client({
      url: `/api/user/getUserAuthToken?authTicketId=${authTicketId}`,
      method: 'GET',
    })) as ApiResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get auth token');
  }
};

/**
 * 绑定用户认证票据
 * @param authTicketId 票据ID
 * @returns Promise<ApiResponse>
 */
export const setUserAuthToken = async (authTicketId: string): Promise<ApiResponse> => {
  try {
    const response = (await client({
      url: `/api/user/setUserAuthToken?authTicketId=${authTicketId}`,
      method: 'POST',
    })) as ApiResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to set auth token');
  }
};
